// Pure MTB planner helpers.
//
// This module is deliberately network-free and side-effect-free so it can be
// unit-tested from fixtures and imported by both the ingest script and the
// running app. It holds the maths that turns raw OSM mountain-bike tags into
// planner inputs: difficulty mapping, ability-neutral ride/climb time
// baselines, and the bike-carrying lift heuristic (Decision 8 of the plan).
//
import { mtbAccess } from "./mtb-access";
import type { Difficulty } from "./graph-types";

// Map OSM mtb:scale (0-6) onto the existing six-tier abstract Difficulty enum,
// so DIFFICULTY_RANK / ABILITY_CEILING and the engine keep working unchanged.
// Colour bands rendered downstream are green / blue / red / black /
// double-black; scales 5 and 6 share the "freeride" tier (pro line).
const SCALE_TO_DIFFICULTY: readonly Difficulty[] = [
  "novice", // 0 -> green
  "easy", // 1 -> blue
  "intermediate", // 2 -> red
  "advanced", // 3 -> black
  "expert", // 4 -> double-black
  "freeride", // 5 -> double-black (pro line)
  "freeride", // 6 -> double-black (pro line)
];

export function mtbScaleToDifficulty(scale: number): Difficulty {
  if (!Number.isFinite(scale)) return "intermediate";
  const floored = Math.floor(scale);
  const clamped = Math.min(SCALE_TO_DIFFICULTY.length - 1, Math.max(0, floored));
  return SCALE_TO_DIFFICULTY[clamped];
}

// Preserve the published IMBA colours in the MTB legend. White and green both
// sit in its easiest (green) bucket. An explicitly blue trail is not red.
// https://wiki.openstreetmap.org/wiki/Key:mtb:scale:imba
// https://www.imba.com/resource/trail-difficulty-rating-system
const IMBA_TO_DIFFICULTY: readonly Difficulty[] = [
  "novice", // 0 white -> easiest bucket
  "novice", // 1 green
  "easy", // 2 blue
  "advanced", // 3 black  -> black
  "expert", // 4 double -> double-black
];

export function imbaScaleToDifficulty(imba: number): Difficulty {
  if (!Number.isFinite(imba)) return "intermediate";
  const floored = Math.floor(imba);
  const clamped = Math.min(IMBA_TO_DIFFICULTY.length - 1, Math.max(0, floored));
  return IMBA_TO_DIFFICULTY[clamped];
}

// IMBA grade -> an equivalent numeric mtb:scale, only for the ride/climb TIME
// baseline (which is keyed on mtb:scale). Difficulty itself uses the table
// above; this is a rough technicality-to-speed parallel.
const IMBA_TO_SCALE: readonly number[] = [0, 1, 2, 3, 4];

// When a way is admitted with NO explicit difficulty grade at all (neither
// mtb:scale nor mtb:scale:imba), never assume green: an unknown bike trail is
// graded a conservative "intermediate" (red). The minimal ingest filter only
// admits graded ways, so this is defence-in-depth, not a routine path.
const UNGRADED_DEFAULT_DIFFICULTY: Difficulty = "intermediate";
const UNGRADED_DEFAULT_SCALE = 2;

// Descent speed (km/h) by mtb:scale, index 0..6. Technical terrain is slower;
// gravity is assumed, so these are not effort-bound.
const DESCENT_KMH: readonly number[] = [22, 18, 14, 11, 9, 7, 6];

// Ability-neutral ride/climb time baselines. The engine scales these later by
// fitness, bike type and group (Phase 3); here they are deliberately neutral.
//
// descentMin is driven by length and technicality. climbMin is effort-bound:
// a baseline vertical-ascent rate (VAM) of 600 m/h versus a flat pace of
// ~9 km/h, whichever dominates. descentM is accepted for signature
// completeness and future use but does not drive the descent time.
export function estRideMinutes(
  lengthM: number,
  mtbScale: number,
  ascentM: number,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for future use
  descentM: number,
): { descentMin: number; climbMin: number } {
  if (!(lengthM > 0)) {
    return { descentMin: 0, climbMin: 0 };
  }

  const scaleIndex = Math.min(
    DESCENT_KMH.length - 1,
    Math.max(0, Math.floor(mtbScale)),
  );
  const descentMin = (lengthM / 1000 / DESCENT_KMH[scaleIndex]) * 60;

  const ascent = Math.max(0, ascentM);
  const climbMin = Math.max((ascent / 600) * 60, (lengthM / 9000) * 60);

  return { descentMin, climbMin };
}

// Normalise a raw OSM aerialway:bike_transport tag: lowercase and trim so
// "Yes", " yes" and "yes" all read as the same value. A reviewer flagged that
// the raw tag was being compared unnormalised; this is the single place that
// fix lives so every caller benefits.
export function normaliseBikeTransportTag(
  value: string | null | undefined,
): string | null {
  if (value === null || value === undefined) return null;
  const trimmed = value.trim().toLowerCase();
  return trimmed.length === 0 ? null : trimmed;
}

// Decision 8: which lifts carry bikes. An explicit OSM
// aerialway:bike_transport tag wins; otherwise we fall back to lift type.
// Enclosed cabins carry bikes; chair lifts only do so when explicitly tagged
// "yes"; surface and tow lifts never do. Unknown types default to false.
const BIKE_CARRYING_LIFT_TYPES: ReadonlySet<string> = new Set([
  "gondola",
  "cable_car",
  "funicular",
  "mixed_lift",
]);

export function allowsBikesHeuristic(
  liftType: string,
  bikeTransportTag: string | null | undefined,
): boolean {
  const tag = normaliseBikeTransportTag(bikeTransportTag);
  if (tag === "yes") return true;
  if (tag === "no") return false;
  return BIKE_CARRYING_LIFT_TYPES.has(liftType);
}

// OSM permits trailing +/- within a grade. Reject mixed values and arbitrary
// prose: extracting the first digit of "1;5" would dangerously under-grade it.
// https://wiki.openstreetmap.org/wiki/Key:mtb:scale
export function parseMtbGrade(value: string | undefined, maximum = 6): number | null {
  if (value === undefined) return null;
  const match = value.trim().match(/^(?:S)?([0-6])[+-]?$/i);
  if (!match) return null;
  const n = Number(match[1]);
  return n <= maximum ? n : null;
}

function nonEmpty(value: string | undefined): string | null {
  if (value === undefined) return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

// Round to one decimal place (the precision of area_trails.est_ride_minutes /
// est_climb_minutes, both NUMERIC(5,1)).
function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

// The MTB-specific column values for an area_trails row, derived purely from
// OSM tags plus the geometry-derived length / ascent / descent. The caller
// supplies length and ascent/descent from the shared DEM pipeline; this
// function owns only the tag interpretation and the time baselines.
export type MtbTrailFields = {
  sport: "mtb";
  difficulty: Difficulty;
  mtb_scale: number | null;
  mtb_scale_uphill: number | null;
  trail_visibility: string | null;
  surface: string | null;
  sac_scale: string | null;
  bidirectional: boolean;
  est_ride_minutes: number;
  est_climb_minutes: number | null;
};

export function osmTagsToMtbTrailFields(
  tags: Record<string, string>,
  lengthM: number,
  ascentM: number,
  descentM: number,
): MtbTrailFields {
  const mtbScale = parseMtbGrade(tags["mtb:scale"]);
  const mtbScaleImba = parseMtbGrade(tags["mtb:scale:imba"], 4);
  const mtbScaleUphill = parseMtbGrade(tags["mtb:scale:uphill"], 5);
  const surface = nonEmpty(tags["surface"]);

  // Use the harder explicit STS/IMBA rating. An admitted-but-ungraded way keeps
  // a conservative intermediate fallback, never an assumed easy rating.
  let difficulty =
    mtbScale !== null
      ? mtbScaleToDifficulty(mtbScale)
      : mtbScaleImba !== null
        ? imbaScaleToDifficulty(mtbScaleImba)
        : UNGRADED_DEFAULT_DIFFICULTY;
  if (mtbScale !== null && mtbScaleImba !== null) {
    // These describe different terrain characteristics. Neither may erase a
    // harder explicit rating, for example a technically easy line with jumps.
    const order: Difficulty[] = ["novice", "easy", "intermediate", "advanced", "expert", "freeride"];
    const imba = imbaScaleToDifficulty(mtbScaleImba);
    if (order.indexOf(imba) > order.indexOf(difficulty)) difficulty = imba;
  }

  // Effective numeric scale for the (scale-keyed) ride/climb TIME baseline only.
  const effectiveScale =
    Math.max(mtbScale ?? 0, mtbScaleImba !== null ? IMBA_TO_SCALE[mtbScaleImba] : 0,
      mtbScale === null && mtbScaleImba === null ? UNGRADED_DEFAULT_SCALE : 0);

  // Uphill traversal requires an explicit uphill scale and permission in both
  // directions. A pedallable surface does not establish access permission.
  const access = mtbAccess(tags);
  const bidirectional = access.forward && access.backward && mtbScaleUphill !== null;

  const { descentMin, climbMin } = estRideMinutes(
    lengthM,
    effectiveScale,
    ascentM,
    descentM,
  );

  return {
    sport: "mtb",
    difficulty,
    mtb_scale: mtbScale,
    mtb_scale_uphill: mtbScaleUphill,
    trail_visibility: nonEmpty(tags["trail_visibility"]),
    surface,
    sac_scale: nonEmpty(tags["sac_scale"]),
    bidirectional,
    est_ride_minutes: round1(descentMin),
    est_climb_minutes: bidirectional ? round1(climbMin) : null,
  };
}

// A way is shared ski/MTB infrastructure when it is tagged as a piste AND it
// is mountain-bike rideable (an explicit mtb:scale, or bicycle=yes). Such ways
// are ingested as two area_trails rows (one per sport) sharing an osm_id.
export function isSharedSkiMtbWay(tags: Record<string, string>): boolean {
  const hasPiste = nonEmpty(tags["piste:type"]) !== null;
  if (!hasPiste) return false;
  const hasMtbScale =
    nonEmpty(tags["mtb:scale"]) !== null || nonEmpty(tags["mtb:scale:imba"]) !== null;
  const bicycleYes = nonEmpty(tags["bicycle"])?.toLowerCase() === "yes";
  return hasMtbScale || bicycleYes;
}

// ---------------------------------------------------------------------------
// Phase 3/4 rider-preference weights (pure, table-driven)
//
// These three helpers convert MTB preferences into per-edge multipliers and an
// ascent budget. They are kept here (alongside the rest of the MTB maths) so
// the itinerary engine and the derive layer can import them without pulling in
// the preferences module's React-free but heavier type surface.
// ---------------------------------------------------------------------------

export type RidingStyle =
  | "flow"
  | "technical-enduro"
  | "downhill-park"
  | "cross-country"
  | "e-bike-friendly";

export type BikeType = "hardtail" | "full-suspension" | "e-bike" | "dh";

export type ClimbingTolerance =
  | "lift-only"
  | "some-pedalling"
  | "happy-to-climb"
  | "maximise";

// The total metres of ascent a rider is willing to pedal in a day, by their
// stated climbing tolerance. The engine uses this as a hard ascent budget for
// the beam search (lift-only riders get no pedal-up legs at all).
const CLIMB_BUDGET_M: Record<ClimbingTolerance, number> = {
  "lift-only": 0,
  "some-pedalling": 300,
  "happy-to-climb": 800,
  maximise: 2500,
};

export function climbBudgetM(climbingTolerance: ClimbingTolerance): number {
  return CLIMB_BUDGET_M[climbingTolerance];
}

// Smooth, flowy surfaces (groomed berms, hardpack and tarmac) versus rough,
// natural surfaces (loose dirt, roots and rock). Used by styleFit to reward or
// penalise a trail's texture against the rider's chosen style.
const SMOOTH_SURFACES: ReadonlySet<string> = new Set([
  "compacted",
  "fine_gravel",
  "asphalt",
  "paved",
  "concrete",
]);
const ROUGH_SURFACES: ReadonlySet<string> = new Set([
  "ground",
  "dirt",
  "earth",
  "rock",
  "rocks",
  "gravel",
  "pebblestone",
]);

function clampFit(value: number): number {
  return Math.min(1.2, Math.max(0.5, value));
}

const DIFFICULTY_TO_STYLE_SCALE: Record<Difficulty, number> = {
  novice: 0,
  easy: 1,
  intermediate: 2,
  advanced: 3,
  expert: 4,
  freeride: 5,
};

function styleScaleForTrail(trail: { mtbScale: number | null; difficulty?: Difficulty | null }): number {
  return trail.mtbScale ?? (trail.difficulty ? DIFFICULTY_TO_STYLE_SCALE[trail.difficulty] : 1);
}

// styleFit scores how well a trail suits the rider's chosen style, returning a
// multiplier in [0.5, 1.2] (1.0 is neutral). The bands below are deliberate and
// calibrated by intent, not measured data: each style rewards the terrain that
// style of riding seeks out and gently penalises the terrain it avoids. When
// mtb:scale is missing but difficulty is known (for IMBA-only trails), use that
// difficulty as the style scale; truly unknown trails stay mild.
export function styleFit(
  trail: {
    mtbScale: number | null;
    difficulty?: Difficulty | null;
    surface: string | null;
    climb?: boolean;
    flatFraction?: number | null;
  },
  style: RidingStyle,
): number {
  const scale = styleScaleForTrail(trail);
  const surface = trail.surface?.toLowerCase() ?? null;
  const smooth = surface !== null && SMOOTH_SURFACES.has(surface);
  const rough = surface !== null && ROUGH_SURFACES.has(surface);
  const isClimb = trail.climb === true;

  let fit = 1;
  switch (style) {
    case "flow": {
      // Flow riders want fast, bermed, low-consequence trails: green/blue/red
      // (scale 1-2) on smooth surfaces. Steep, rough double-blacks are the
      // opposite of flow.
      if (scale <= 2) fit += 0.15;
      if (smooth) fit += 0.1;
      if (scale >= 4) fit -= 0.3;
      if (rough && scale >= 3) fit -= 0.1;
      break;
    }
    case "technical-enduro": {
      // Enduro riders seek rough, steep, technical descents (scale 3-5). A
      // smooth green is too tame to be the point.
      if (scale >= 3) fit += 0.15;
      if (rough) fit += 0.1;
      if (scale <= 1) fit -= 0.25;
      if (smooth && scale <= 1) fit -= 0.05;
      break;
    }
    case "downhill-park": {
      // Park riders want gravity descents (scale 2-5); a pedal-up climb edge is
      // dead weight for them.
      if (isClimb) {
        fit -= 0.4;
      } else {
        if (scale >= 2 && scale <= 5) fit += 0.15;
        if (scale >= 4) fit += 0.05;
      }
      break;
    }
    case "cross-country": {
      // XC riders favour rolling, longer trails and are happy to climb; very
      // gnarly descents (scale 4+) are not their terrain.
      if (isClimb) fit += 0.15;
      if (scale <= 2) fit += 0.1;
      if (scale >= 4) fit -= 0.3;
      break;
    }
    case "e-bike-friendly": {
      // Neutral: the e-bike's climbing is handled by climbFactor, not here.
      fit = 1;
      break;
    }
  }
  return clampFit(fit);
}

// bikeAllowsTrail is a SOFT suitability multiplier in [0.5, 1.0] (never a hard
// block) reflecting that a bike can ride anything, just better or worse. The
// engine still routes over a penalised edge if nothing better is reachable.
export function bikeAllowsTrail(
  trail: { mtbScale: number | null; difficulty?: Difficulty | null; climb?: boolean },
  bikeType: BikeType,
): number {
  const scale = styleScaleForTrail(trail);
  const isClimb = trail.climb === true;
  switch (bikeType) {
    case "dh":
      // A downhill rig is brilliant pointed downhill and miserable pedalling up.
      return isClimb ? 0.5 : 1;
    case "hardtail":
      // A hardtail is undergunned on rough (scale 4+) descents; fine elsewhere.
      return !isClimb && scale >= 4 ? 0.7 : 1;
    case "full-suspension":
      return 1;
    case "e-bike":
      // Climbs are handled by climbFactor, so the bike itself is neutral here.
      return 1;
    default:
      return 1;
  }
}
